import { useEffect, useMemo, useState } from 'react';
import { act, formatNumber } from '../lib/actions';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import { Drawer, Field, inputClass, Pill } from './admin/kit';
import { Button, Icon, Panel } from './ui';

export interface Callback {
  id: string;
  queueId: string | null;
  queueName: string | null;
  customerId: string | null;
  number: string;
  name: string | null;
  note: string;
  dueAt: string;
  source: 'agent' | 'queue' | 'rule';
  assignedTo: string | null;
  assignedName: string | null;
  status: 'pending' | 'offered' | 'calling' | 'done' | 'failed' | 'cancelled';
  offeredTo: string | null;
  offeredName: string | null;
  attempts: number;
  maxAttempts: number;
  retryMinutes: number;
  lastResult: string | null;
  fromInteractionId: string | null;
  callInteractionId: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  closedAt: string | null;
}

const STATUS: Record<Callback['status'], { label: string; tone: 'line' | 'good' | 'bad' | 'warn' }> = {
  pending: { label: 'Waiting', tone: 'line' },
  offered: { label: 'Offered', tone: 'warn' },
  calling: { label: 'Calling', tone: 'warn' },
  done: { label: 'Reached', tone: 'good' },
  failed: { label: 'Not reached', tone: 'bad' },
  cancelled: { label: 'Cancelled', tone: 'line' },
};
const SOURCE: Record<Callback['source'], string> = { agent: 'Scheduled', queue: 'Caller pressed 1', rule: 'Queue rule' };

const open = (c: Callback) => ['pending', 'offered', 'calling'].includes(c.status);

function due(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((d.getTime() - Date.now()) / 60_000);
  const at = d.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  if (mins <= 0) return `Due now (${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`;
  if (mins < 60) return `In ${mins} min`;
  return at;
}

/** Callbacks for the signed-in person (or everyone's, for supervisors), kept fresh from callback events. */
export function useCallbacks(query: string): [Callback[] | null, () => void] {
  const [list, setList] = useState<Callback[] | null>(null);
  const fresh = useNova((s) => s.feed.find((e) => e.type.startsWith('callback.'))?.eventId);
  const load = () => void api<Callback[]>('GET', `/callbacks?${query}`).then(setList, () => setList([]));
  useEffect(load, [query, fresh]);
  return [list, load];
}

async function run(path: string, body: unknown, ok: string): Promise<boolean> {
  return Boolean(await act(() => api('POST', path, body ?? {}).then(() => true), ok));
}

/**
 * A due callback offered to this agent: who to call and why, with Call now,
 * Later, Someone else and Not needed. It goes to another agent after a
 * minute if nothing is chosen.
 */
export function CallbackOffers() {
  const me = useNova((s) => s.user?.sub);
  const hasPhone = useNova((s) => Boolean(s.user && s.people[s.user.sub]?.extension));
  const [list] = useCallbacks('status=open&mine=1');
  const offers = (list ?? []).filter((c) => c.status === 'offered' && c.offeredTo === me);
  if (!hasPhone || !offers.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-40 flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2">
      {offers.map((c) => (
        <OfferCard key={c.id} c={c} />
      ))}
    </div>
  );
}

function OfferCard({ c }: { c: Callback }) {
  const [busy, setBusy] = useState(false);
  const go = async (fn: () => Promise<boolean>) => {
    setBusy(true);
    await fn();
    setBusy(false);
  };
  return (
    <section role="alertdialog" aria-label={`Callback to ${c.name ?? formatNumber(c.number)}`} className="rounded-xl border border-ion/50 bg-hull p-4 shadow-2xl">
      <p className="text-[12px] font-medium uppercase tracking-wide text-ion">Callback due</p>
      <p className="mt-1 font-semibold">{c.name ?? 'Customer'}</p>
      <p className="tabular text-sm text-ink-2">{formatNumber(c.number)}</p>
      <p className="mt-1 text-[12px] text-ink-3">
        {SOURCE[c.source]}
        {c.queueName ? ` · ${c.queueName}` : ''}
        {c.attempts ? ` · try ${c.attempts + 1} of ${c.maxAttempts}` : ''}
        {c.lastResult ? ` · last: ${c.lastResult.toLowerCase()}` : ''}
      </p>
      {c.note ? <p className="mt-2 rounded-lg bg-abyss/50 p-2 text-sm">{c.note}</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button tone="primary" className="px-3 py-1.5 text-sm" disabled={busy} onClick={() => void go(() => run(`/callbacks/${c.id}/call`, {}, 'Calling…'))}>
          <Icon name="phone" size={14} /> Call now
        </Button>
        <Button tone="quiet" className="px-3 py-1.5 text-sm" disabled={busy} onClick={() => void go(() => run(`/callbacks/${c.id}/later`, { minutes: 15 }, 'Callback moved 15 minutes later'))}>
          In 15 min
        </Button>
        {!c.assignedTo ? (
          <Button tone="ghost" className="px-2 py-1.5 text-sm" disabled={busy} onClick={() => void go(() => run(`/callbacks/${c.id}/pass`, {}, 'Offered to someone else'))}>
            Someone else
          </Button>
        ) : null}
        <Button tone="ghost" className="ml-auto px-2 py-1.5 text-sm" disabled={busy} onClick={() => void go(() => run(`/callbacks/${c.id}/close`, { result: 'cancelled' }, 'Callback closed'))}>
          Not needed
        </Button>
      </div>
    </section>
  );
}

interface VoiceQueue {
  id: string;
  name: string;
  channel: string;
}

function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Scheduling a callback: who to call, when, by whom (me, a queue, or, for supervisors, someone), and why. */
export function ScheduleCallback({ number = '', name = '', fromInteractionId, onClose }: { number?: string; name?: string; fromInteractionId?: string; onClose: () => void }) {
  const role = useNova((s) => s.user?.role);
  const sup = role === 'supervisor' || role === 'manager' || role === 'admin' || role === 'platform_admin';
  const people = useNova((s) => s.people);
  const [queues, setQueues] = useState<VoiceQueue[]>([]);
  const [f, setF] = useState({ number, name, note: '', when: 'now', at: localInput(new Date(Date.now() + 3_600_000)), who: 'me', queueId: '', personId: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<VoiceQueue[]>('GET', '/queues').then((q) => setQueues(q.filter((x) => x.channel === 'voice')), () => setQueues([]));
  }, []);
  const callers = useMemo(() => Object.values(people).filter((p) => p.extension).sort((a, b) => a.name.localeCompare(b.name)), [people]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const dueAt = (): string => {
    const now = new Date();
    if (f.when === 'now') return now.toISOString();
    if (f.when === '1h') return new Date(now.getTime() + 3_600_000).toISOString();
    if (f.when === 'tomorrow') {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      t.setHours(9, 0, 0, 0);
      return t.toISOString();
    }
    return new Date(f.at).toISOString();
  };
  const ready = f.number.replace(/\D/g, '').length >= 3 && (f.who !== 'queue' || f.queueId) && (f.who !== 'person' || f.personId) && (f.when !== 'at' || f.at);
  async function save() {
    setBusy(true);
    const ok = await act(
      () =>
        api('POST', '/callbacks', {
          number: f.number,
          name: f.name || undefined,
          note: f.note || undefined,
          dueAt: dueAt(),
          assignTo: f.who === 'me' ? 'me' : f.who === 'person' ? f.personId : null,
          queueId: f.who === 'queue' ? f.queueId : null,
          ...(fromInteractionId ? { fromInteractionId } : {}),
        }),
      'Callback scheduled',
    );
    setBusy(false);
    if (ok) onClose();
  }
  return (
    <Drawer
      title="Schedule a callback"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !ready} onClick={() => void save()}>
            Schedule
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Number to call" hint="With the country code, for example +254 712 345 678">
        {(id) => <input id={id} inputMode="tel" className={`${inputClass} tabular`} value={f.number} onChange={(e) => set('number', e.target.value)} />}
      </Field>
      <Field label="Name (optional)">{(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} />}</Field>
      <Field label="When">
        {(id) => (
          <select id={id} className={inputClass} value={f.when} onChange={(e) => set('when', e.target.value)}>
            <option value="now">As soon as possible</option>
            <option value="1h">In an hour</option>
            <option value="tomorrow">Tomorrow at 09:00</option>
            <option value="at">At a time I choose</option>
          </select>
        )}
      </Field>
      {f.when === 'at' ? <Field label="Date and time">{(id) => <input id={id} type="datetime-local" className={inputClass} value={f.at} onChange={(e) => set('at', e.target.value)} />}</Field> : null}
      <Field label="Who calls" hint="A queue's callback goes to whoever takes that queue and is free when it is due.">
        {(id) => (
          <select id={id} className={inputClass} value={f.who} onChange={(e) => set('who', e.target.value)}>
            <option value="me">Me</option>
            <option value="queue">Anyone in a queue</option>
            {sup ? <option value="person">Someone else</option> : null}
          </select>
        )}
      </Field>
      {f.who === 'queue' ? (
        <Field label="Queue">
          {(id) => (
            <select id={id} className={inputClass} value={f.queueId} onChange={(e) => set('queueId', e.target.value)}>
              <option value="">Choose a queue</option>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {f.who === 'person' ? (
        <Field label="Person">
          {(id) => (
            <select id={id} className={inputClass} value={f.personId} onChange={(e) => set('personId', e.target.value)}>
              <option value="">Choose someone</option>
              {callers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.extension})
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      <Field label="Note (optional)" hint="What the call is about, for whoever calls">
        {(id) => <textarea id={id} rows={3} className={inputClass} value={f.note} maxLength={1000} onChange={(e) => set('note', e.target.value)} />}
      </Field>
      <p className="text-[12px] text-ink-3">If nobody answers, Nova tries again 15 minutes later, up to 3 tries in all.</p>
    </Drawer>
  );
}

/** Reports > Voice > Callbacks: the open ones first, then what happened to the rest. Agents see theirs. */
export function CallbacksPage() {
  const role = useNova((s) => s.user?.role);
  const sup = role === 'supervisor' || role === 'manager' || role === 'admin' || role === 'platform_admin';
  const [status, setStatus] = useState('open');
  const [mine, setMine] = useState(false);
  const [list, reload] = useCallbacks(`status=${status}${mine ? '&mine=1' : ''}`);
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<Callback | null>(null);
  return (
    <Panel className="p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium">Callbacks</h1>
          <p className="text-sm text-ink-3">{sup ? "Everyone's callbacks: scheduled by agents, asked for by callers in a queue, or taken by a queue rule." : 'Callbacks for you, and the ones you scheduled.'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-line bg-abyss/60 px-3 py-2 text-sm">
            <option value="open">Open</option>
            <option value="done">Reached</option>
            <option value="failed">Not reached</option>
            <option value="cancelled">Cancelled</option>
            <option value="all">All</option>
          </select>
          {sup ? (
            <label className="flex items-center gap-1.5 text-sm text-ink-2">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Mine only
            </label>
          ) : null}
          <Button tone="primary" className="px-3 py-2 text-sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} /> New callback
          </Button>
        </div>
      </div>
      {list === null ? (
        <p className="py-8 text-center text-sm text-ink-3">Loading…</p>
      ) : list.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-2">No callbacks here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-sm" aria-label="Callbacks">
            <thead className="text-ink-3">
              <tr className="border-b border-line">
                <th className="py-2 pr-3 font-normal">Due</th>
                <th className="py-2 pr-3 font-normal">Customer</th>
                <th className="py-2 pr-3 font-normal">For</th>
                <th className="py-2 pr-3 font-normal">Status</th>
                <th className="py-2 pr-3 font-normal">Tries</th>
                <th className="py-2 pr-3 font-normal">Note</th>
                <th className="py-2 font-normal" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b border-line/60 align-top">
                  <td className="py-2.5 pr-3 text-ink-2">{open(c) ? due(c.dueAt) : new Date(c.closedAt ?? c.dueAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="py-2.5 pr-3">
                    <span className="font-medium">{c.name ?? 'Customer'}</span>
                    <span className="tabular block text-[12px] text-ink-3">{formatNumber(c.number)}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-ink-2">
                    {c.assignedName ?? c.queueName ?? '—'}
                    <span className="block text-[12px] text-ink-3">{SOURCE[c.source]}</span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <Pill tone={STATUS[c.status].tone}>{STATUS[c.status].label}</Pill>
                    {c.status === 'offered' || c.status === 'calling' ? <span className="block text-[12px] text-ink-3">{c.offeredName}</span> : null}
                    {c.lastResult && c.status !== 'done' ? <span className="block text-[12px] text-ink-3">{c.lastResult}</span> : null}
                  </td>
                  <td className="tabular py-2.5 pr-3">
                    {c.attempts}/{c.maxAttempts}
                  </td>
                  <td className="max-w-[240px] py-2.5 pr-3 text-[12px] text-ink-2">{c.note}</td>
                  <td className="py-2.5 text-right">
                    {open(c) && c.status !== 'calling' ? (
                      <span className="flex flex-wrap justify-end gap-1">
                        <Button tone="quiet" className="px-2.5 py-1 text-xs" onClick={() => void run(`/callbacks/${c.id}/call`, {}, 'Calling…').then(reload)}>
                          Call now
                        </Button>
                        <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => setMoving(c)}>
                          Change
                        </Button>
                        <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => void run(`/callbacks/${c.id}/close`, { result: 'done' }, 'Marked as reached').then(reload)}>
                          Reached
                        </Button>
                        <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => void run(`/callbacks/${c.id}/close`, { result: 'cancelled' }, 'Callback cancelled').then(reload)}>
                          Cancel
                        </Button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding ? (
        <ScheduleCallback
          onClose={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
      {moving ? (
        <ChangeCallback
          c={moving}
          sup={sup}
          onClose={() => {
            setMoving(null);
            reload();
          }}
        />
      ) : null}
    </Panel>
  );
}

/** When it is due, who calls it, and the note. */
function ChangeCallback({ c, sup, onClose }: { c: Callback; sup: boolean; onClose: () => void }) {
  const people = useNova((s) => s.people);
  const me = useNova((s) => s.user?.sub);
  const [queues, setQueues] = useState<VoiceQueue[]>([]);
  const [at, setAt] = useState(localInput(new Date(Math.max(Date.now(), new Date(c.dueAt).getTime()))));
  const [who, setWho] = useState(c.assignedTo ? `u:${c.assignedTo}` : c.queueId ? `q:${c.queueId}` : `u:${me}`);
  const [note, setNote] = useState(c.note);
  useEffect(() => {
    api<VoiceQueue[]>('GET', '/queues').then((q) => setQueues(q.filter((x) => x.channel === 'voice')), () => setQueues([]));
  }, []);
  const callers = Object.values(people)
    .filter((p) => p.extension && (sup || p.id === me))
    .sort((a, b) => a.name.localeCompare(b.name));
  async function save() {
    const [kind, id] = who.split(':');
    const ok = await act(
      () => api('PATCH', `/callbacks/${c.id}`, { dueAt: new Date(at).toISOString(), note, assignTo: kind === 'u' ? id : null, queueId: kind === 'q' ? id : c.queueId }),
      'Callback changed',
    );
    if (ok) onClose();
  }
  return (
    <Drawer
      title={`Callback to ${c.name ?? formatNumber(c.number)}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" onClick={() => void save()}>
            Save
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Due">{(id) => <input id={id} type="datetime-local" className={inputClass} value={at} onChange={(e) => setAt(e.target.value)} />}</Field>
      <Field label="Who calls">
        {(id) => (
          <select id={id} className={inputClass} value={who} onChange={(e) => setWho(e.target.value)}>
            <optgroup label="Anyone in a queue">
              {queues.map((q) => (
                <option key={q.id} value={`q:${q.id}`}>
                  {q.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="One person">
              {callers.map((p) => (
                <option key={p.id} value={`u:${p.id}`}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          </select>
        )}
      </Field>
      <Field label="Note">{(id) => <textarea id={id} rows={3} className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
    </Drawer>
  );
}
